import { useState } from 'react';
import { MessageSquarePlus, Search, Trash2, Edit2, Check, X, ChevronDown, PanelLeftClose, Home } from 'lucide-react';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { SearchableBrandSelect } from './SearchableBrandSelect';
import { cn } from '../lib/utils';

export default function Sidebar({ isOpen, onClose }) {
  const { 
    threads, 
    currentThread, 
    selectedBrand, 
    brands,
    setSelectedBrand,
    loadThreads,
    createThread, 
    selectThread, 
    deleteThread, 
    renameThread 
  } = useChat();
  const { isSuperAdmin } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const filteredThreads = threads.filter((thread) =>
    thread.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNewChat = async () => {
    await createThread();
  };

  const handleSelectBrand = async (brand) => {
    if (!brand) {
      // Home selected - go back to landing page
      setSelectedBrand(null);
      window.location.href = '/';
      return;
    }
    setSelectedBrand(brand);
    await loadThreads(brand.brand_id);
  };

  const handleStartEdit = (thread) => {
    setEditingId(thread.thread_id);
    setEditTitle(thread.title);
  };

  const handleSaveEdit = async (threadId) => {
    if (editTitle.trim()) {
      await renameThread(threadId, editTitle.trim());
    }
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  // Group threads by date
  const groupedThreads = filteredThreads.reduce((groups, thread) => {
    const date = new Date(thread.updated_at || thread.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let group;
    if (date.toDateString() === today.toDateString()) {
      group = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      group = 'Yesterday';
    } else if (date > new Date(today.setDate(today.getDate() - 7))) {
      group = 'Previous 7 Days';
    } else {
      group = 'Older';
    }
    
    if (!groups[group]) groups[group] = [];
    groups[group].push(thread);
    return groups;
  }, {});

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 w-72 bg-background/80 backdrop-blur-xl border-r border-border/50 flex flex-col transition-transform duration-300 lg:relative lg:translate-x-0',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        {/* Mobile close button row */}
        <div className="flex items-center justify-between mb-3 lg:hidden">
          <span className="text-sm font-medium text-muted-foreground">Menu</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Brand Selector - Searchable */}
        <SearchableBrandSelect
          brands={brands}
          selectedBrand={selectedBrand}
          onSelect={(brand) => handleSelectBrand(brand)}
          placeholder="Select Brand"
          className="mb-3"
          showIndustry={true}
          showHomeOption={isSuperAdmin}
        />

        {/* New Chat Button */}
        <Button
          onClick={handleNewChat}
          className="w-full gap-2"
          data-testid="new-chat-btn"
        >
          <MessageSquarePlus className="w-4 h-4" />
          New Chat
        </Button>
      </div>

      {/* Search */}
      <div className="p-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-threads"
          />
        </div>
      </div>

      {/* Threads List */}
      <ScrollArea className="flex-1 px-2">
        <div className="space-y-4 py-2">
          {Object.entries(groupedThreads).map(([group, groupThreads]) => (
            <div key={group}>
              <h3 className="text-xs font-semibold text-muted-foreground px-3 mb-2">
                {group}
              </h3>
              <div className="space-y-1">
                {groupThreads.map((thread) => (
                  <div
                    key={thread.thread_id}
                    className={cn(
                      'group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all',
                      currentThread?.thread_id === thread.thread_id
                        ? 'bg-gradient-to-r from-primary/10 to-transparent border-l-2 border-primary'
                        : 'hover:bg-muted/50'
                    )}
                    onClick={() => editingId !== thread.thread_id && selectThread(thread)}
                    data-testid={`thread-${thread.thread_id}`}
                  >
                    {editingId === thread.thread_id ? (
                      <div className="flex-1 flex items-center gap-1">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="h-7 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(thread.thread_id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          data-testid="edit-thread-input"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleSaveEdit(thread.thread_id)}
                        >
                          <Check className="w-3 h-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={handleCancelEdit}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 truncate text-sm">
                          {thread.title || 'New Chat'}
                        </span>
                        <div className="hidden group-hover:flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(thread);
                            }}
                            data-testid={`edit-thread-${thread.thread_id}`}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteThread(thread.thread_id);
                            }}
                            data-testid={`delete-thread-${thread.thread_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {filteredThreads.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">
              {searchQuery ? 'No chats found' : 'No chats yet'}
            </p>
          )}
        </div>
      </ScrollArea>

      {/* Mobile close overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden"
          style={{ zIndex: -1 }}
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
    </aside>
  );
}
